import React, { useEffect, useState, useRef } from 'react';
import ReactApexChart from 'react-apexcharts';
import axiosConn from 'config/axiosConn';

// Define the themes array
const themes = [
    'Adopt Healthy Lifestyles', 
    'Adopt sustainable food systems', 
    'Reduce E-Waste', 
    'Reduce Waste', 
    'Save Energy', 
    'Save Water', 
    'Say No to Single Use Plastic'
];

const ApexChartHeatmap = () => {
    const [chartOptions, setChartOptions] = useState(null);
    const [selectedData, setSelectedData] = useState(null);
    const [states, setStates] = useState([]);
    const [loading, setLoading] = useState(true); 
    const chartRef = useRef(null);

    const [minValue, setMinValue] = useState(0);
    const [maxValue, setMaxValue] = useState(0);

    useEffect(() => {
        const fetchTop10States = async () => {
            try {
                const response = await fetch('https://ecoclubs.education.gov.in/api/adminHeat-map', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' }
                });

                if (response.ok) {
                    const data = await response.json();
                    const stateNames = data.map(item => item.state);
                    setStates(stateNames);
                } else {
                    console.error('Error fetching data: HTTP status', response.status);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchTop10States();
    }, []);

    useEffect(() => {
        if (states.length > 0) {
            const fetchEventData = async () => {
                try {
                    const response = await axiosConn('post', 'adminHeat-map', { states });
                    if (response && response.data) {
                        const allThemes = [];
    
                        response.data.forEach(stateData => {
                            const { state, themes } = stateData;
    
                            themes.forEach(theme => {
                                const { theme_name, eventCount } = theme;
                                allThemes.push({ state, theme_name, eventCount });
                            });
                        });
    
                        const newSeries = themes.map((theme) => ({
                            name: theme,
                            data: states.map((state) => {
                                const stateData = allThemes.filter(item => item.state === state && item.theme_name === theme);
                                return !isNaN(stateData[0]?.eventCount) ? stateData[0].eventCount : 0;
                            })
                        }));
    
                        const allEventCounts = allThemes.map(item => item.eventCount);
                        const min = Math.min(...allEventCounts);
                        const max = Math.max(...allEventCounts);
    
                        setMinValue(min);
                        setMaxValue(max);
    
                        const initialChartOptions = {
                            series: newSeries,
                            options: {
                                chart: {
                                    height: 350,
                                    type: 'heatmap',
                                    events: {
                                        dataPointSelection: async function (event, chartContext, config) {
                                            const selectedState = config.w.config.xaxis.categories[config.dataPointIndex];
                                            const selectedTheme = config.w.config.series[config.seriesIndex].name;
                                            const data = await fetchData(selectedState, selectedTheme);
                                            setSelectedData(data);
                                        },
                                    },
                                },
                                plotOptions: {
                                    heatmap: {
                                        shadeIntensity: 0.1,
                                        radius: 0,
                                        colorScale: {
                                            ranges: [
                                                { from: min, to: min + (max - min) * 0.2, name: 'Low', color: '#FFB300' },
                                                { from: min + (max - min) * 0.2, to: min + (max - min) * 0.4, name: 'Medium-Low', color: '#FF9800' },
                                                { from: min + (max - min) * 0.4, to: min + (max - min) * 0.6, name: 'Medium', color: '#FF4500' },
                                                { from: min + (max - min) * 0.6, to: max, name: 'High', color: '#D32F2F' },
                                            ],
                                        },
                                    },
                                },
                                dataLabels: { enabled: true },
                                stroke: { width: 1 },
                                title: {
                                    text: 'States/UTs/ABs (Top 10) vs Eco Clubs for Mission LiFE theme',
                                    align: 'left',
                                    offsetX: 20,
                                    style: { fontSize: '20px', fontWeight: 'bold' },
                                },
                                tooltip: { enabled: false },
                                xaxis: {
                                    type: 'category',
                                    categories: states,
                                    title: { text: 'Top 10 States/UTs/ABs' },
                                    labels: { style: { fontSize: '10px' } },
                                },
                                yaxis: {
                                    title: { text: 'Themes' },
                                    categories: themes,
                                    labels: {
                                        style: { fontSize: '10px' },
                                        offsetX: -1,
                                        formatter: (value) => value.length > 20 ? value.slice(0, 20) + '...' : value,
                                    },
                                },
                            },
                        };
    
                        setChartOptions(initialChartOptions);
                    }
                } catch (error) {
                    console.error('Error fetching event data:', error);
                }
            };
    
            fetchEventData();
        }
    }, [states, minValue, maxValue]);
    

    const fetchData = async (state, theme_name) => {
        try {
            const response = await axiosConn('post', 'top10-SchoolEvents', { state, theme_name });
            console.log('response:', response); 
            if (response.status === 200) {
                const topDistricts = response.data.topDistricts.map(item => ({
                    name: item.district,
                    value: item.eventCount,
                }));
    
                const topSchools = response.data.topSchools.map(item => ({
                    name: item.school_name,
                    value: item.eventCount,
                    district: item.district,
                })); 
    
                // const districtsCount = topDistricts.length;
                // const schoolsCount = topSchools.length;
                // const totalCount = topSchools.reduce((sum, school) => sum + school.value, 0);
    
                console.log('Selected data:', {
                    state,
                    districts: topDistricts,
                    schools: topSchools,
                    theme: theme_name,
                });
    
                // Return the data instead of setting it to state
                return {
                    state,
                    districts: topDistricts,
                    schools: topSchools,
                    theme: theme_name,
                };
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    
        // Return empty data if there was an error
        return {
            state,
            districts: [],
            schools: [],
            theme: theme_name,
        };
    };
    

    const resetSelection = () => {
        console.log('Chart options:', chartOptions);
        if (chartRef.current || chartOptions) {
            console.log('resetSelection called');
            setSelectedData(null);
        }
    };

    

    return (
        <div style={{ position: 'relative', marginBottom: '22px' }}>
            {chartOptions && (
                <div id="chart">
                    <ReactApexChart
                        ref={chartRef}
                        options={chartOptions.options}
                        series={chartOptions.series}
                        type="heatmap"
                        height={350}
                    />
                </div>
            )}

            {selectedData && selectedData.districts.length > 0 && !loading && (
                <div style={{ marginTop: '20px' }}>
                    {/* {console.log('selectedData------------------:', selectedData)} */}
                    <h4 style={{ marginTop: '20px', fontWeight: 'bold' }}>Top Districts</h4>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={{ border: '1px solid #ddd', padding: '8px', fontWeight: 'bold' }}>District</th>
                                <th style={{ border: '1px solid #ddd', padding: '8px', fontWeight: 'bold' }}>Event Count</th>
                            </tr>
                        </thead>
                        <tbody>
                            {selectedData.districts.map((district, index) => (
                                <tr key={index}>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{district.name}</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{district.value}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <h4 style={{ marginTop: '20px', fontWeight: 'bold' }}>Top Schools</h4>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={{ border: '1px solid #ddd', padding: '8px', fontWeight: 'bold' }}>School</th>
                                <th style={{ border: '1px solid #ddd', padding: '8px', fontWeight: 'bold' }}>Event Count</th>
                            </tr>
                        </thead>
                        <tbody>
                            {selectedData.schools.map((school, index) => (
                                <tr key={index}>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{school.name}</td>
                                    <td style={{ border: '1px solid #ddd', padding: '8px' }}>{school.value}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <button
                className="resetButtonbtn"
                onClick={resetSelection}
                style={{
                    position: 'absolute',
                    top: '1px',
                    right: '50px',
                    padding: '5px 10px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                }}
            >
                Reset
            </button>
        </div>
    );
};

export default ApexChartHeatmap;

